import { css } from '@emotion/react';
import React, { Key } from 'react';
import { Checkbox } from '../checkbox';
import {
  COLUMN_MIN_WIDTH,
  EXPAND_COLUMN_WIDTH,
  TABLE_CONTROL_CELL_HORIZONTAL_PADDING,
  TABLE_CONTROL_HEADER_VERTICAL_PADDING,
} from './constants';
import { TableCol } from './TableCol';
import { TableRow } from './TableRow';
import {
  DataTableProps,
  SelectRowsAction,
  DataTableColumnGroupType,
  DataTableColumnType,
  DataTableRowSelectionRecord,
} from './types';
import { getFixedMergeColumn, getGroupColumns } from './utils';

export interface DataTableHeaderProps<DataType>
  extends Pick<
    DataTableProps<DataType>,
    | 'columns'
    | 'rows'
    | 'sortKey'
    | 'sortDirection'
    | 'onSortChange'
    | 'selectableRows'
    | 'hasExpandButton'
    | 'renderExpandedRow'
    | 'hasSelectAll'
    | 'resizable'
  > {
  stickyHeader?: boolean;
  stickyOffset?: number;
  allSelected?: boolean;
  selectedRows?: Map<Key, DataTableRowSelectionRecord<DataType>>;
  onSelectedRows?: (action: SelectRowsAction<DataType>) => void;
}

export const DataTableHeader = <DataType extends object>({
  columns,
  rows,
  stickyHeader,
  stickyOffset,
  sortKey,
  sortDirection,
  allSelected,
  selectedRows,
  selectableRows,
  hasExpandButton,
  renderExpandedRow,
  hasSelectAll,
  resizable,
  onSelectedRows,
  onSortChange,
}: DataTableHeaderProps<DataType>) => {
  const computedGroupColumns = getGroupColumns({ columns });
  const isColumnGroup = columns.some((column) => {
    const columnGroup = (column as DataTableColumnGroupType<DataType>).columnGroup;
    return columnGroup && columnGroup.length > 0;
  });

  const SelectionColumn = () => {
    const selectionProps = {
      stickyHeader,
      stickyOffset,
      align: 'center' as const,
      verticalAlign: 'middle' as const,
      lastRow: isColumnGroup,
      ...(columns[0]?.fixed && { fixed: true, fixedColWidth: 0, width: COLUMN_MIN_WIDTH }),
      ...(isColumnGroup && { rowSpan: 2 }),
    };
    const indeterminate = selectedRows && selectedRows.size > 0 && !allSelected;

    return (
      <TableCol
        {...selectionProps}
        css={css`
          padding: ${TABLE_CONTROL_HEADER_VERTICAL_PADDING}px ${TABLE_CONTROL_CELL_HORIZONTAL_PADDING}px;
        `}
      >
        {/* hasSelectAll이 명시적으로 false일 때만 전체 선택 UI를 숨깁니다. */}
        {hasSelectAll === false ? null : (
          <Checkbox
            name='all-row-select'
            indeterminate={indeterminate}
            disabled={rows.length === 0 || rows.every((row) => row.disabled || !(row.selectable ?? true))}
            checked={allSelected}
            onChange={() => onSelectedRows?.({ type: 'SELECT_ROWS', rows })}
            css={css`
              justify-content: center;
            `}
          />
        )}
      </TableCol>
    );
  };

  const ExpandableColumn = () => {
    const expandableProps = {
      stickyHeader,
      stickyOffset,
      align: 'center' as const,
      verticalAlign: 'middle' as const,
      lastRow: isColumnGroup,
      ...(columns[0]?.fixed && {
        fixed: true,
        fixedColWidth: selectableRows ? COLUMN_MIN_WIDTH : 0,
        width: EXPAND_COLUMN_WIDTH,
      }),
      ...(isColumnGroup && { rowSpan: 2 }),
    };

    return <TableCol {...expandableProps} />;
  };

  return (
    <>
      <TableRow>
        {!!selectableRows && <SelectionColumn />}
        {renderExpandedRow && hasExpandButton && <ExpandableColumn />}
        {columns.map((column, index) => {
          const baseColumn = column as DataTableColumnType<DataType>;
          const columnGroup = (column as DataTableColumnGroupType<DataType>).columnGroup;
          if (baseColumn) {
            const fixedColumnProps =
              columnGroup?.length > 0
                ? {
                    fixed: false,
                  }
                : {
                    ...(baseColumn.fixed && {
                      fixedColWidth: getFixedMergeColumn(
                        columns,
                        !!selectableRows,
                        index,
                        !!renderExpandedRow && Boolean(hasExpandButton),
                      ),
                    }),
                  };

            const columnGroupProps = columnGroup
              ? { ...(columnGroup?.length > 0 && { colSpan: columnGroup?.length }) }
              : { ...(isColumnGroup && { rowSpan: 2, lastRow: true }) };

            const tableColProps = {
              stickyHeader,
              stickyOffset,
              sortKey,
              sortDirection,
              resizable,
              ...baseColumn,
              ...columnGroupProps,
              ...fixedColumnProps,
            };

            return (
              <TableCol
                key={index}
                {...tableColProps}
                onClick={() => tableColProps.sortable && onSortChange?.(tableColProps.id)}
                sortIndicatorProps={{
                  isSorted: tableColProps.sortKey === tableColProps.id && tableColProps.sortDirection !== 'default',
                  // @ts-expect-error: TS2322
                  sortDirection: tableColProps.sortDirection,
                }}
              >
                {tableColProps.text}
              </TableCol>
            );
          }
        })}
      </TableRow>
      {computedGroupColumns.length > 0 && (
        <TableRow>
          {computedGroupColumns.map((column, index) => {
            const lastThHasRowspan =
              (columns[columns.length - 1] as DataTableColumnGroupType<DataType>).columnGroup?.length > 0;

            const subColumn = column as DataTableColumnType<DataType>;
            if (subColumn) {
              const subTableColProps = {
                stickyHeader,
                stickyOffset,
                sortKey,
                sortDirection,
                resizable,
                ...subColumn,
                ...{ groupColumn: stickyHeader },
              };

              const hasRightBorder = index === computedGroupColumns.length - 1 && !lastThHasRowspan;
              return (
                <TableCol
                  key={index}
                  {...subTableColProps}
                  onClick={() => subTableColProps.sortable && onSortChange?.(subTableColProps.id)}
                  sortIndicatorProps={{
                    isSorted:
                      subTableColProps.sortKey === subTableColProps.id && subTableColProps.sortDirection !== 'default',
                    // @ts-expect-error: TS2322
                    sortDirection: subTableColProps.sortDirection,
                  }}
                  hasRightBorder={hasRightBorder}
                >
                  {subTableColProps.text}
                </TableCol>
              );
            }
          })}
        </TableRow>
      )}
    </>
  );
};
